import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordPage() {
  const { token } = useAuth();
  const { isHindi } = useLanguage();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [visible, setVisible] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.newPassword.length < 8 || form.newPassword.length > 72) {
      toast.error(isHindi ? 'पासवर्ड 8 से 72 अक्षरों का होना चाहिए।' : 'Password must be between 8 and 72 characters.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast.error(isHindi ? 'नए पासवर्ड मेल नहीं खाते।' : 'New passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: form.currentPassword,
        new_password: form.newPassword,
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(isHindi ? 'पासवर्ड बदल दिया गया।' : 'Password changed successfully.');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setVisible({ current: false, next: false, confirm: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || (isHindi ? 'पासवर्ड बदलने में त्रुटि।' : 'Could not change password.'));
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = 'mt-1 w-full border border-gray-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#121212]';
  const passwordField = (key, label, value, onChange, autoComplete, constraints = {}) => (
    <label className="block text-sm font-semibold text-gray-800">
      {label}
      <span className="relative mt-1 block">
        <input required autoComplete={autoComplete} type={visible[key] ? 'text' : 'password'} value={value} onChange={onChange}
          className={`${fieldClass} pr-11`} {...constraints} />
        <button type="button" onClick={() => setVisible(p => ({ ...p, [key]: !p[key] }))}
          aria-label={visible[key] ? 'Hide password' : 'Show password'} aria-pressed={visible[key]}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800">
          {visible[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </span>
    </label>
  );

  return (
    <div className="min-h-screen bg-white px-4 py-10">
      <section className="max-w-lg mx-auto border border-gray-200 bg-white p-6 sm:p-8">
        <h1 className={`text-2xl font-bold text-[#121212] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
          {isHindi ? 'पासवर्ड बदलें' : 'Change password'}
        </h1>
        <p className={`mt-2 mb-6 text-sm text-gray-600 ${isHindi ? 'font-hindi' : ''}`}>
          {isHindi ? 'अपना अस्थायी पासवर्ड बदलने के लिए वर्तमान पासवर्ड दर्ज करें।' : 'Enter your current password, then choose a new one.'}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {passwordField('current', isHindi ? 'वर्तमान पासवर्ड' : 'Current password', form.currentPassword,
            e => setForm(p => ({ ...p, currentPassword: e.target.value })), 'current-password')}
          {passwordField('next', isHindi ? 'नया पासवर्ड' : 'New password', form.newPassword,
            e => setForm(p => ({ ...p, newPassword: e.target.value })), 'new-password', { minLength: 8, maxLength: 72 })}
          {passwordField('confirm', isHindi ? 'नए पासवर्ड की पुष्टि करें' : 'Confirm new password', form.confirmPassword,
            e => setForm(p => ({ ...p, confirmPassword: e.target.value })), 'new-password', { minLength: 8, maxLength: 72 })}
          <p className="text-xs text-gray-500">{isHindi ? 'पासवर्ड 8 से 72 अक्षरों का होना चाहिए।' : 'Use 8 to 72 characters.'}</p>
          <Button type="submit" disabled={saving} className="w-full bg-[#121212] text-white hover:bg-black">
            {saving ? (isHindi ? 'सहेज रहे हैं…' : 'Saving…') : (isHindi ? 'पासवर्ड अपडेट करें' : 'Update password')}
          </Button>
        </form>
      </section>
    </div>
  );
}
